import React from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { styles } from "./style";

export interface UnderlineTabItem<T> {
	value: T;
	label: string;
	/** Rendered before the label. */
	icon?: React.ReactNode;
	/** Rendered after the label, e.g. a count. */
	adornment?: React.ReactNode;
}

interface Props<T> {
	tabs: UnderlineTabItem<T>[];
	value: T;
	onChange: (value: T) => void;
	"aria-label": string;
	/** Scroll the row sideways when it is wider than its container, e.g. on a phone. */
	scrollable?: boolean;
}

const useStyles = makeStyles()(styles);

// MUI Tabs supplies the tablist/tab roles, aria-selected, roving tabindex and arrow-key
// navigation; only its look is replaced here.
export const UnderlineTabs = <T extends string | number>({
	tabs,
	value,
	onChange,
	"aria-label": ariaLabel,
	scrollable = false,
}: Props<T>) => {
	const { classes, cx } = useStyles();
	return (
		<Mui.Tabs
			aria-label={ariaLabel}
			value={value}
			onChange={(event, next) => onChange(next as T)}
			variant={scrollable ? "scrollable" : "standard"}
			scrollButtons={scrollable ? "auto" : false}
			allowScrollButtonsMobile={scrollable}
			classes={{
				root: cx(classes.root, scrollable ? classes.scrollableRoot : classes.fixedRoot),
				flexContainer: classes.flexContainer,
				indicator: classes.indicator,
			}}
		>
			{tabs.map((tab) => (
				<Mui.Tab
					key={tab.value}
					value={tab.value}
					label={
						<span className={classes.label}>
							{tab.icon}
							<span className={classes.text} data-label={tab.label}><span>{tab.label}</span></span>
							{tab.adornment}
						</span>
					}
					disableRipple
					className={classes.tab}
				/>
			))}
		</Mui.Tabs>
	);
};
